import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  SecurityContext,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ApiService} from '../services/api-service';

import {environment} from '../../environments/environment';
import {MarkdownModule, MarkdownService, SECURITY_CONTEXT} from 'ngx-markdown';
import {CommonModule, ViewportScroller} from '@angular/common';
import {Signin} from '../signin/signin';
import {Shared} from '../services/shared';
import {Router} from '@angular/router';
import {AuthService} from '../services/auth';
import {TranslatePipe} from '@ngx-translate/core';
import {of} from 'rxjs';
import {SummarizerInfo} from '../summarizer-info/summarizer-info';

declare var bootstrap: any;
@Component({
  selector: 'app-camera',
  standalone: true,
  imports: [
    SummarizerInfo,
    CommonModule,
    MarkdownModule,
    TranslatePipe
  ],
  providers: [MarkdownService,  { provide: SECURITY_CONTEXT, useValue: SecurityContext.HTML }],
  templateUrl: './camera.html',
  encapsulation: ViewEncapsulation.None,
  styleUrl: './camera.scss'
})
export class Camera implements OnDestroy, OnInit {
  env = environment;

  useKey = 0;

  @ViewChild('video') videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;
  photo: string | null = null;
  output = ''
  file: File | null = null;
  captured = false;
  prompted = false;
  completed = false;
  apiService = inject(ApiService)
  renderedOutput: string = '';
  useCamera = false;
  modalVisible = false;
  started = false;
  photoSlotsCount = 0;
  hideMultiImageButtons = false;

  video_or_image = false;

  selectedOption = 'summarize';  // default selected

  show1 = true;
  show2 = true;
  show3 = true;

  hasCamera = true;

  onSelect(option: string) {
    // console.log(option)
    this.selectedOption = option;
  }

  thumbnails: string[] = [];  // starts empty

  // Example method to add thumbnails dynamically later
  addThumbnail(url: string) {
    this.thumbnails.push(url);
  }


  authService = inject(AuthService)

  router = inject(Router)
  collapseDropdownMenu() {
    this.sharedService.requestCloseDropdown();
  }

  private stream: MediaStream | null = null;

  token = true;
  isLogged = false;

  @ViewChild(Signin) signIn!: Signin;

  private sharedService = inject(Shared)

  tokenChange() {
    this.token = false;
  }

  isLoggedIn(): boolean {
    const token = this.authService.getToken();
    if (!token) {
      this.isLogged = false;
      return false;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp;
      if (!exp) {
        this.isLogged = false;
        return false;
      }
      const now = Math.floor(Date.now() / 1000);
      this.isLogged = exp > now;
      return this.isLogged;
    } catch (e) {
      this.isLogged = false;
      return false;
    }
  }

  @ViewChild('top') top!: ElementRef;

  private scrollToTop(): void {
    setTimeout(() => {
      const y = this.top.nativeElement.getBoundingClientRect().top + window.pageYOffset - 56;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }, 200); // I changed 0.2 to 200 ms for a more realistic delay
  }

  @ViewChild('bottom') private bottom!: ElementRef;

  private scrollToBottom(): void {
    setTimeout(() => {
      this.bottom.nativeElement.scrollIntoView({ behavior: 'smooth' });
    }, 0.2);
  }

  videoStream!: MediaStream;
  async initCamera() {
    this.video_or_image = true;
    // Stop previous stream if it exists
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
    }

    try {
      this.photo = null
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      this.videoRef.nativeElement.srcObject = stream;
      this.videoStream = stream; // Save the stream so we can stop it later
      this.useCamera = true;
      this.collapseDropdownMenu();
      this.started = true;

      this.captured = false;
    } catch (err) {
      console.error('Error accessing camera', err);
    }
  }

  photoSlots: (string | null)[] = Array(6).fill(null);
  slot = 0;
  capture() {
    const video = this.videoRef.nativeElement;
    const canvas = this.canvasRef.nativeElement;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    const imageData = canvas.toDataURL('image/jpeg');
    this.photo = canvas.toDataURL('image/jpeg');
    this.addImageToPhotoSlots(this.photo)

    this.captured = true;
    this.stopCamera();
  }

  addImageToPhotoSlots(imageData: any) {
    const emptyIndex = this.photoSlots.findIndex(photo => photo === null);
    if (emptyIndex !== -1) {
      this.photoSlots[emptyIndex] = imageData;
      this.photoSlotsCount += 1;
      if (this.photoSlotsCount == 2 && !this.isLoggedIn()) {
        this.hideMultiImageButtons = true
      }

      if (this.photoSlotsCount == 6 && this.isLoggedIn()) {
        this.hideMultiImageButtons = true
      }

    } else {
      // Optional: Replace the last one or show a message
      console.warn("Maximum number of photos reached");
    }
  }

  stopCamera() {
    if (this.videoStream) {
      this.videoStream.getTracks().forEach(track => track.stop());
    }
  }
  uploadPhoto(event: Event) {
    this.video_or_image = true;
    this.collapseDropdownMenu();
    this.started = true;
    this.stopCamera()
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    const reader = new FileReader();

    reader.onload = () => {
      this.photo = reader.result as string;
      this.addImageToPhotoSlots(this.photo)
      this.captured = true;
      // Reset input value so same file can be selected again
      input.value = '';
    };


    reader.readAsDataURL(file);

  }

  retry() {
    this.captured = false;
    this.prompted = false;
    this.completed = false;
    this.photo = null;
    this.output = ''
    this.file = null;
    this.useCamera = false;
    this.started = false;
    this.stopCamera();
    this.sharedService.callNavbarFunction();
    this.photoSlots = Array(6).fill(null);
    this.photoSlotsCount = 0;
    this.hideMultiImageButtons = false;
    this.video_or_image = false;
    this.scrollToTop();
  }
  upload() {
    if (this.isLoggedIn()) {
      this.started = true;
      this.stopCamera();
      this.prompted = true;
      if (!this.photo) return;

      // Convert base64 to Blob
      const byteString = atob(this.photo.split(',')[1]);
      const mimeString = this.photo.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], {type: mimeString});

      // Optional: convert Blob to File if needed
      const file = new File([blob], 'photo.jpg', {type: mimeString});

      const formData = new FormData();
      // formData.append('file', file);

      this.photoSlots.forEach((dataUrl, index) => {
        if (dataUrl) {
          const [header, base64Data] = dataUrl.split(',');
          const mimeMatch = header.match(/data:(.*);base64/);
          const mimeType = mimeMatch ? mimeMatch[1] : 'image/png'; // fallback

          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });

          const file = new File([blob], `photo-${index + 1}.jpg`, { type: mimeType });
          formData.append('files', file);
        }
      });

      this.uploadFile2(formData)
    } else {
      // check if counter is 2
      // console.log('else:' + this.useKey)
      if (localStorage.getItem('ZBsfo0vR8DwIqZdGF0Zab3cdQ') === null) {
        localStorage.removeItem('ZBsfo0vR8DwIqZdGF0Zab3cdQ');
        localStorage.setItem('ZBsfo0vR8DwIqZdGF0Zab3cdQ', '0');
      }
      let storedValue = localStorage.getItem('ZBsfo0vR8DwIqZdGF0Zab3cdQ')
      this.useKey = storedValue !== null ? parseInt(storedValue, 10) : 2;
      // console.log(this.useKey)
      if (this.useKey < 1 && this.useKey > -1) {
        // use 4b model free for 1 times

        this.useKey = this.useKey + 1;
        localStorage.removeItem('ZBsfo0vR8DwIqZdGF0Zab3cdQ');
        localStorage.setItem('ZBsfo0vR8DwIqZdGF0Zab3cdQ', this.useKey.toString());
        this.started = true;
        this.stopCamera();
        this.prompted = true;
        if (!this.photo) return;

        // Convert base64 to Blob
        const byteString = atob(this.photo.split(',')[1]);
        const mimeString = this.photo.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], {type: mimeString});

        // Optional: convert Blob to File if needed
        const file = new File([blob], 'photo.jpg', {type: mimeString});

        const formData = new FormData();


        // formData.append('file', file);


        this.photoSlots.forEach((dataUrl, index) => {
          if (dataUrl) {
            const [header, base64Data] = dataUrl.split(',');
            const mimeMatch = header.match(/data:(.*);base64/);
            const mimeType = mimeMatch ? mimeMatch[1] : 'image/png'; // fallback

            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeType });

            const file = new File([blob], `photo-${index + 1}.jpg`, { type: mimeType });
            formData.append('files', file);

          }
        });

        this.uploadFile11(formData)

      } else {
        // open sign up screen
        // const btn = document.querySelector('[data-bs-toggle="modal"][data-bs-target="#exampleModal"]') as HTMLElement;
        // if (btn) btn.click();
        // else console.warn('Button not found');
        this.stopCamera()
        // route to plans
        this.router.navigate(['/plans'])

      }
    }
  }

  insertZeroWidthSpaces(text: string) {
    // Add zero-width space every 20 chars if no spaces found (for very long strings)
    text.replace(/[\u200B-\u200D\uFEFF]/g, '');
    return text.replace(/(\S{20})(?=\S)/g, '$1\u200B');
  }

  uploadFile2(formData: FormData) {
    this.sharedService.callNavbarFunction();
    this.output = '';
    this.completed = false;

    const token = this.authService.getToken();

    //  add language
    const browserLanguage = (navigator.language || 'en').split('-')[0];

    formData.append('language', browserLanguage);
    formData.append('prompt_type', this.selectedOption);

    fetch(this.env.apiBaseUrl + '/upload22', {
      method: 'POST',
      body: formData,
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'Accept': 'text/event-stream'
      }
    })
      .then(async response => {
        if (response.status === 403) {
          try {
            const json = await response.json();
            this.output = json.detail || 'Not enough credits to continue.';
          } catch {
            this.output = 'Not enough credits to continue.';
          }
          this.completed = true;
          return;
        }

        if (!response.ok || !response.body) {
          this.output = `Error ${response.status}: ${response.statusText}`;
          this.completed = true;
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let partial = '';

        while (true) {
          try {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            partial += chunk;

            const lines = partial.split('\n');
            partial = lines.pop() || ''; // save partial line

            for (const line of lines) {
              if (line.startsWith('data: ')) {

                const word = line.replace('data: ', '').trim();
                if (word === '[DONE]') {
                  this.completed = true;
                  this.sharedService.callNavbarFunction();
                  return;
                } else {


// When appending output:
//                   this.output += this.insertZeroWidthSpaces(word.replace(/<\|newline\|>/g, '\n\n'));

                  const cleanedWord = this.normalizeChunk(word.replace(/<\|newline\|>/g, '\n\n'));
                  this.output += this.insertZeroWidthSpaces(cleanedWord);

                  this.scrollToBottom();
                }
              }
            }
          } catch (err) {
            console.error('Read error:', err);
            this.output = 'Oops, something went wrong...';
            this.completed = true;
            return;
          }
        }
      })
      .catch(err => {
        console.error('Fetch failed:', err);
        this.output = 'Oops, something went wrong...';
        this.completed = true;
      });
  }

  normalizeChunk(text: string): string {
    // Replace multiple spaces with a single space
    let normalized = text.replace(/\s+/g, ' ');

    // Optional: fix spaces around punctuation (remove space before punctuation)
    normalized = normalized.replace(/\s+([.,!?:])/g, '$1');

    // Trim leading and trailing spaces
    normalized = normalized.trim();

    return normalized;
  }

  uploadFile11(formData: FormData) {
    this.sharedService.callNavbarFunction();
    this.output = '';
    this.completed = false;

    //  add language
    const browserLanguage = (navigator.language || 'en').split('-')[0];

    formData.append('language', browserLanguage);
    formData.append('prompt_type', this.selectedOption);

    fetch(this.env.apiBaseUrl + '/upload11', {
      method: 'POST',
      body: formData,
      headers: {
        'Accept': 'text/event-stream',
        // Authorization header commented out as in original code
        // Uncomment below if you want to add token:
        // ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    })
      .then(async response => {
        if (response.status === 403) {
          try {
            const json = await response.json();
            this.output = json.detail || 'Not enough credits to continue.';
          } catch {
            this.output = 'Not enough credits to continue.';
          }
          this.completed = true;
          return;
        }

        if (!response.ok || !response.body) {
          this.output = `Error ${response.status}: ${response.statusText}`;
          this.completed = true;
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let partial = '';

        while (true) {
          try {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            partial += chunk;

            const lines = partial.split('\n');
            partial = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const word = line.replace('data: ', '').trim();
                if (word === '[DONE]') {
                  this.completed = true;
                  this.sharedService.callNavbarFunction();
                  return;
                } else {
                  this.output += word.replace(/<\|newline\|>/g, '\n\n');
                  this.scrollToBottom();
                }
              }
            }
          } catch (err) {
            console.error('Read error:', err);
            this.output = 'Oops, something went wrong...';
            this.completed = true;
            return;
          }
        }
      })
      .catch(err => {
        console.error('Fetch failed:', err);
        this.output = 'Oops, something went wrong...';
        this.completed = true;
      });
  }


  async ngOnInit() {
    if (localStorage.getItem('ZBsfo0vR8DwIqZdGF0Zab3cdQ') === null) {
      localStorage.removeItem('ZBsfo0vR8DwIqZdGF0Zab3cdQ');
      localStorage.setItem('ZBsfo0vR8DwIqZdGF0Zab3cdQ', '0');
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      this.hasCamera = devices.some(device => device.kind === 'videoinput');
    } catch (err) {
      console.error('Error detecting media devices:', err);
      this.hasCamera = false;
    }
  }
  ngOnDestroy(): void {
    this.stopCamera();
  }

  getChatStream() {
    const source = new EventSource(this.env.apiBaseUrl + "/chat");

    source.onmessage = (event) => {
      // console.log("message:", event.data);

      if (event.data === "[DONE]") {
        // console.log("Stream done, closing connection");
        source.close();  // Cleanly close from client side
      }
    };

    source.onerror = (err) => {
      console.error("Stream error:", err);
      source.close();  // Always close on error to prevent zombie connections
    };
  }

  protected readonly of = of;
}

interface ChatResponseData {
  status_code: number;
  response_body: {
    id: string;
    object: string;
    created: number;
    model: string;
    choices: [
      {
        index: number;
        logprobs: null | any; // Use 'any' or a more specific type if you know the structure of logprobs
        finish_reason: string;
        message: {
          role: string;
          content: string;
        };
      }
    ];
    usage: {
      prompt_tokens: number;
      completion_tokens: number;
      total_tokens: number;
    };
    stats: any; // Use 'any' or a more specific type if you know the structure of stats
    system_fingerprint: string;
  };
}
